import React from 'react'
import { useTranslation } from 'react-i18next'

interface Lead {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  description: string | null
  status_id: number
  status_name: string
  status_color: string
  urgency_id: number
  urgency_name: string
  urgency_color: string
  priority: 'low' | 'medium' | 'high' | null
  best_time_to_contact: 'day' | 'evening' | 'weekend' | null
  task_count: number
  created_at: string
  creator_first_name: string
  creator_last_name: string
  assigned_to?: number
  assigned_to_name?: string
  assigned_at?: string
  partner_id?: number | null
  partner_name?: string | null
  partner_email?: string | null
}

interface LeadsTableViewProps {
  leads: Lead[]
  onLeadClick: (lead: Lead) => void
  onFollowUpClick: (lead: Lead) => void
  onConvertClick: (lead: Lead) => void
  loading?: boolean
}

const LeadsTableView: React.FC<LeadsTableViewProps> = ({
  leads,
  onLeadClick,
  onFollowUpClick,
  onConvertClick,
  loading = false
}) => {
  const { t, i18n } = useTranslation()

  // Translation functions for status and urgency values
  const translateStatusName = (statusName: string): string => {
    const statusMap: { [key: string]: string } = {
      'New': t('leads.statusNew'),
      'Called': t('leads.statusCalled'),
      'Emailed': t('leads.statusEmailed'),
      'Waiting': t('leads.statusWaiting'),
      'Qualified': t('leads.statusQualified'),
      'Not Interested': t('leads.statusNotInterested'),
      'new': t('leads.statusNew'),
      'called': t('leads.statusCalled'),
      'emailed': t('leads.statusEmailed'),
      'waiting': t('leads.statusWaiting'),
      'qualified': t('leads.statusQualified'),
      'not interested': t('leads.statusNotInterested'),
    }
    return statusMap[statusName] || statusName
  }

  const translateUrgencyName = (urgencyName: string): string => {
    const urgencyMap: { [key: string]: string } = {
      'Low': t('leads.urgencyLow'),
      'Medium': t('leads.urgencyMedium'),
      'High': t('leads.urgencyHigh'),
      'low': t('leads.urgencyLow'),
      'medium': t('leads.urgencyMedium'),
      'high': t('leads.urgencyHigh'),
    }
    return urgencyMap[urgencyName] || urgencyName
  }

  const translatePriorityName = (priorityName: string): string => {
    const priorityMap: { [key: string]: string } = {
      'low': t('leads.low'),
      'medium': t('leads.medium'),
      'high': t('leads.high'),
    }
    return priorityMap[priorityName] || priorityName
  }

  const getDisplayName = (lead: Lead) => {
    const firstName = lead.first_name || ''
    const lastName = lead.last_name || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || lead.email || 'Unnamed Lead'
  }

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading leads...</p>
        </div>
      </div>
    )
  }

  if (leads.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-8 text-center">
          <div className="text-gray-400 mb-4">🎯</div>
          <p className="text-gray-600 dark:text-gray-400">No leads found matching your criteria</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.lead')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.contactInfo')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.urgency')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.priority')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.tasks')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.created')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.assignedTo')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                {t('table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {leads.map((lead) => (
              <tr
                key={lead.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                onClick={() => onLeadClick(lead)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-white">
                        {getDisplayName(lead).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {getDisplayName(lead)}
                      </div>
                      {lead.description && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {lead.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {lead.email && (
                      <div className="flex items-center mb-1">
                        <span className="mr-1">📧</span>
                        <span className="truncate max-w-xs">{lead.email}</span>
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-center">
                        <span className="mr-1">📞</span>
                        <span>{lead.phone}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    style={{
                      backgroundColor: `${lead.status_color}20`,
                      color: lead.status_color
                    }}
                  >
                    {translateStatusName(lead.status_name)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    style={{
                      backgroundColor: `${lead.urgency_color}20`,
                      color: lead.urgency_color
                    }}
                  >
                    {translateUrgencyName(lead.urgency_name)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {lead.priority && (
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(lead.priority)}`}>
                      {translatePriorityName(lead.priority)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {lead.task_count}
                    </span>
                    {lead.task_count > 0 && (
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                        {lead.task_count !== 1 ? t('leads.tasks') : t('leads.task')}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div>{formatDate(lead.created_at)}</div>
                  <div className="text-xs">
                    {t('table.by')} {lead.creator_first_name} {lead.creator_last_name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {lead.assigned_to_name ? (
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {lead.assigned_to_name}
                      </div>
                      {lead.assigned_at && (
                        <div className="text-xs">
                          {formatDate(lead.assigned_at)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 italic">{t('table.unassigned')}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onFollowUpClick(lead)
                      }}
                      className="text-gray-900 hover:text-blue-900 dark:text-gray-600 dark:hover:text-blue-300 transition-colors"
                      title="Schedule Follow-up"
                    >
                      📅
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onConvertClick(lead)
                      }}
                      className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 transition-colors"
                      title="Convert to Customer"
                    >
                      ✅
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default LeadsTableView
